import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'
import { getActivePod } from '@/lib/pod'
/**
 * Public privacy policy (/privacy). No auth, no Clerk — a plain document served
 * by the same SPA as the chat, so a React Router <Link> from the chat footer
 * resolves here on whatever domain serves the app.
 *
 * WHY THIS EXISTS: the assistant captures a buyer's name, phone, and enquiry the
 * moment they signal purchase intent. UK GDPR Art.13 requires telling the person
 * how their data is handled at the point of collection. This page is that notice;
 * the chat footer links to it.
 *
 * BRAND IS PER-POD: this component is shared by every pod, so the header brand
 * comes from the active pod (getActivePod().brandName) — never hardcoded — and
 * the body refers to the operator (Autaimate) and "a business" generically, so
 * no pod's name leaks onto another pod's site.
 *
 * The content is written to the ACTUAL data flows in this codebase (chat →
 * Anthropic; enquiry → D1 + the business; callback texts → Twilio; dashboard →
 * Clerk/Stripe), so it makes no promise the system doesn't keep. Rights are
 * exercised by emailing the contact below — a valid mechanism; a self-serve
 * erasure control is a later convenience, not a requirement for this to be true.
 *
 * NOT LEGAL ADVICE / PENDING REVIEW: this is a strong first version to be
 * reviewed by a UK data-protection specialist before it is relied upon. The
 * controller/processor boundary (the business is the controller of a buyer's
 * enquiry; the operator is the processor) is stated plainly but is exactly the
 * kind of nuance that review settles.
 */
export default function Privacy() {
  const brandName = getActivePod().brandName
  return (
    <div className="min-h-screen bg-surface-base">
      <header className="border-b border-border-subtle bg-surface-raised">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <div>
            <p className="font-display font-semibold tracking-tight">{brandName}</p>
            <p className="text-xs text-text-muted">Privacy Policy</p>
          </div>
          <Link
            to="/"
            className="text-sm text-brand-primary underline underline-offset-2 hover:no-underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 rounded"
          >
            Back to the assistant
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="font-display text-3xl font-semibold tracking-tight text-text-primary">
          Privacy Policy
        </h1>
        <p className="mt-2 text-sm text-text-muted">Last updated: 8 October 2026</p>
        <div className="mt-8 stack-y-8">
          <Section title="Who we are">
            <P>
              This assistant is operated by Michael Stevenson (trading as Autaimate), a sole trader
              based in England. Address: 49 Vicarage Road, St Austell, PL24 2PH.
              We are registered with the Information Commissioner's Office (ICO) under reference{' '}
              <strong>ZC115961</strong>.
            </P>
            <P>
              Michael Stevenson also owns <strong>Cornwall Play Safety Ltd</strong> (company number{' '}
              <strong>17399978</strong>), a separate business that uses this assistant on its own
              website and messaging channels. The two are different legal entities: Autaimate builds
              and runs the assistant, and Cornwall Play Safety Ltd is one of the businesses that uses
              it. We mention this because some of the messaging channels the assistant works on —
              WhatsApp and Facebook Messenger — are registered to Cornwall Play Safety Ltd, so you
              may see that name rather than Autaimate's when you contact a business through them.
            </P>
            <P>
              You can contact us about anything in this policy, or to exercise your rights, at{' '}
              <A href="mailto:mick@autaimate.com">mick@autaimate.com</A>.
            </P>
          </Section>
          <Section title="Our role, and the business's">
            <P>
              This assistant is added to a business's own website to answer visitors' questions and
              capture enquiries. When you chat with the assistant on a business's site,{' '}
              <strong>that business is the data controller</strong> of the enquiry you make — they
              decide how your details are used to follow up with you. Autaimate provides and runs the
              assistant on their behalf, acting as their <strong>data processor</strong>. This policy
              explains, for transparency, how data is handled across the service. The business may
              also have its own privacy policy covering how it uses your enquiry after we pass it on.
            </P>
          </Section>
          <Section title="What we collect">
            <P>When you use the assistant, we handle:</P>
            <List
              items={[
                <>
                  <strong>What you type.</strong> Each message you send is passed to the AI model so
                  it can answer you. The conversation is <strong>not kept</strong> unless you leave
                  your details to be contacted (it is then kept with your enquiry), or ask something
                  the assistant cannot answer (that one question is then kept, word for word, without
                  anything saying who asked it, so the business can answer it for the next person).
                </>,
                <>
                  <strong>Your enquiry details.</strong> When you give your name and phone number,
                  the assistant records an enquiry so the business can get back to you: your name
                  and number, what you're enquiring about and any details you gave, your area, when
                  you'd like a callback, an indicative price if one came up, a priority the
                  assistant gave it, and a suggested opening line for the business's call.
                </>,
                <>
                  <strong>Texts we send you.</strong> If you leave your number, the business may text
                  you: a thank-you, a reminder after a day, a check-in after a week and a
                  follow-up after six weeks, while your enquiry is still open; and, only if you
                  become their customer, once to ask for a review. Every one says how to stop them:{' '}
                  <strong>reply STOP</strong> and that business will send you no more of them,
                  unless you reply START. When you give your number in the chat, it tells you
                  this, and you can say there that you don't want texts: you'll get none about
                  that enquiry. If you tell it you don't want to be contacted at all, your
                  enquiry isn't recorded. Saying it in your own words (&ldquo;please stop
                  texting me&rdquo;) works as STOP does, and the business is shown your message.
                </>,
                <>
                  <strong>Technical data.</strong> Your IP address is used only to limit how fast
                  messages can be sent, to protect the service from abuse. It is never stored.
                </>,
                <>
                  <strong>If you phone and we miss the call.</strong> Where a business uses our
                  missed-call service, we text you back with a link to the assistant, and keep a
                  record that you called, as a code made from your number rather than the number
                  itself. If you have replied STOP to that business, we don&rsquo;t text you back.
                </>,
                <>
                  <strong>If you message on WhatsApp or Facebook Messenger.</strong> Where a business
                  uses those channels, Meta passes your messages to us and our replies back. We keep
                  when you last messaged, as a code made from your number or Messenger ID rather than
                  the number or ID itself; your messages are kept only as described under "What you
                  type".
                </>,
                <>
                  <strong>Linking your contacts.</strong> So the business sees one history rather
                  than scattered pieces, your enquiry, texts, missed calls and messages with that
                  business are grouped in a case file, under a code made from your number. The code
                  cannot be turned back into your number without a key only we hold.
                </>,
              ]}
            />
            <P>
              If you are a business using the dashboard, we also handle your account email (for
              sign-in) and subscription details (for billing).
            </P>
          </Section>

          <Section title="Why we use it, and our lawful basis">
            <List
              items={[
                <>
                  <strong>To answer your questions</strong>, so the assistant can help you. This is
                  necessary for the service you're using.
                </>,
                <>
                  <strong>To pass your enquiry to the business and follow it up</strong> so they can
                  call you back. When you give your details to be contacted, you're asking for that
                  follow-up; the business relies on its legitimate interest in responding to a
                  genuine enquiry, and on the "soft opt-in" for the later texts, which is why every
                  one of them lets you stop them.
                </>,
                <>
                  <strong>To keep the service secure and available</strong>, for example limiting
                  how fast messages can be sent, under our legitimate interest in preventing abuse.
                </>,
              ]}
            />
          </Section>

          <Section title="Who we share it with">
            <P>
              We use a small number of providers to run the service. Each processes data only for
              the function below:
            </P>
            <List
              items={[
                <>
                  <strong>The business</strong>: your enquiry (name, phone, what you need, and the
                  conversation) so they can respond to you. Your details are also sent by text, so
                  the business can call you back quickly.
                </>,
                <>
                  <strong>Anthropic</strong> (United States) provides the AI model that writes the
                  assistant's replies. It stores data in the United States, and its model may run in
                  other countries. It deletes what it receives within 30 days (up to 2 years if it is
                  flagged for breaking Anthropic's usage rules), and does not train its models on it.
                </>,
                <>
                  <strong>Cloudflare</strong> hosts the service and stores our records in the
                  European Union. Its AI service, which helps find the right answer, gives no
                  location guarantee; it does not train its models on what it sees.
                </>,
                <>
                  <strong>Twilio</strong> (United States) carries our texts. Twilio also uses message
                  information for its own purposes, including preventing spam and fraud (which can
                  involve training its own systems to spot them) and improving its services, as its
                  own privacy notice explains.
                </>,
                <>
                  <strong>Meta</strong>: where a business uses WhatsApp or Facebook Messenger, Meta
                  carries those messages between you and the business, exactly as it would for any
                  message you send a business on those apps. For Messenger, Meta acts under its own
                  terms with you.
                </>,
                <>
                  <strong>Speechmatics</strong> transcribes voice answers recorded by a business
                  owner. This is the owner's own voice, not yours.
                </>,
                <>
                  <strong>Clerk</strong> and <strong>Stripe</strong> handle sign-in and billing for
                  businesses using the dashboard, never for people making enquiries.
                </>,
              ]}
            />
            <P>We do not sell your data, and we do not use it for advertising.</P>
          </Section>

          <Section title="Where your data is processed">
            <P>
              Our records are stored in the European Union, which UK law recognises as protecting
              data adequately. Anthropic, Twilio and Meta process data outside the UK and EU, and
              Cloudflare's AI service gives no guarantee of where it runs. Where
              they do, we rely on the safeguards in their own terms that UK data protection law
              recognises (such as the UK Addendum to the EU Standard Contractual Clauses, or the UK
              Extension to the EU–US Data Privacy Framework). You can ask us for detail about a
              specific transfer using the contact details above.
            </P>
          </Section>

          <Section title="How long we keep it">
            <P>These are deleted automatically, every night, when their time is up:</P>
            <List
              items={[
                'your enquiry, the conversation that came with it, and the texts it planned: two years from when you made that enquiry;',
                'a question the assistant could not answer: one year. If the business answered it, its wording is kept as part of the business’s answers for as long as the business uses the assistant;',
                'the record of a missed call or a WhatsApp or Messenger conversation: 90 days;',
                'the case file linking your contacts: until the last thing in it has gone, and at most two years.',
              ]}
            />
            <P>
              If you replied STOP, we keep that (as a code, not your number) until you reply START or
              the business stops using the assistant, so you are not sent follow-up texts again.
            </P>
            <P>
              If you'd like your data deleted sooner, ask the business, or contact us and we'll pass
              your request to them. Deleted records stay in our database provider's rolling 30-day
              history until they age out.
            </P>
          </Section>

          <Section title="Your rights">
            <P>Under UK data protection law you have the right to:</P>
            <List
              items={[
                'access the personal data we hold about you;',
                'ask us to correct it if it is wrong, or complete it if it is incomplete;',
                'ask us to delete it;',
                'ask us to restrict or object to how we use it;',
                'ask for a copy in a portable format, where that right applies.',
              ]}
            />
            <P>
              To exercise any of these, email <A href="mailto:mick@autaimate.com">mick@autaimate.com</A>{' '}
              or ask the business directly. The business can download everything held about your
              enquiry in one file, and delete it at once. Because the business is the controller of
              an enquiry you made to them, we may pass your request to them or act on their
              instructions.
            </P>
            <P>
              If you're unhappy with how your data has been handled, you can complain to the ICO at{' '}
              <A href="https://ico.org.uk/make-a-complaint/">ico.org.uk</A>, though we'd appreciate
              the chance to put things right first.
            </P>
          </Section>

          <Section title="Cookies">
            <P>
              The assistant itself sets no cookies and stores nothing on your device: your conversation is
              held only in the open page. The dashboard uses a sign-in cookie so businesses can stay
              logged in.
            </P>
          </Section>

          <Section title="Changes to this policy">
            <P>
              We may update this policy from time to time. The date at the top shows when it last
              changed.
            </P>
          </Section>
          <Section title="Contact">
            <P>
              Michael Stevenson (trading as Autaimate)
              <br />
              49 Vicarage Road, St Austell, PL24 2PH
              <br />
              <A href="mailto:mick@autaimate.com">mick@autaimate.com</A>
              <br />
              ICO registration: ZC115961
            </P>
          </Section>
        </div>
        <div className="mt-10 border-t border-border-subtle pt-6">
          <Link
            to="/"
            className="text-sm text-brand-primary underline underline-offset-2 hover:no-underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 rounded"
          >
            Back to the assistant
          </Link>
        </div>
      </main>
    </div>
  )
}
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="font-display text-lg font-semibold tracking-tight text-text-primary">
        {title}
      </h2>
      <div className="mt-2 stack-y-3">{children}</div>
    </section>
  )
}
function P({ children }: { children: ReactNode }) {
  return <p className="text-sm leading-relaxed text-text-primary">{children}</p>
}
function List({ items }: { items: ReactNode[] }) {
  return (
    <ul className="list-disc stack-y-1.5 pl-5 text-sm leading-relaxed text-text-primary">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  )
}
function A({ href, children }: { href: string; children: ReactNode }) {
  const external = href.startsWith('http')
  return (
    <a
      href={href}
      {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
      className="text-brand-primary underline underline-offset-2 hover:no-underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 rounded"
    >
      {children}
    </a>
  )
}
